// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import { IconMemo } from "@insite/mobius/Icon/Icon";
import { DataTableCell, DataTableRow, Icon, Link, Typography } from "@insite/mobius/index";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Tooltip, { TooltipPresentationProps } from "@insite/mobius/Tooltip";
import Button from "@store/MobiusOverrides/Button/Button";
import { getDocumentsWrapper } from "@store/Framework/Common/Utilities/getDocumentsWrapper";
import useTrackFileDownload from "@store/Framework/GoogleAnalytics/hooks/useTrackFileDownload";
import useTrackLink from "@store/Framework/GoogleAnalytics/hooks/useTrackLink";
import { getInvoicePdf, getShippingDocuments } from "@store/Framework/Services/documentService";
import Status from "@store/Library/Components/Status";
import Loader from "@store/Library/Components/Toolkit/Loader";
import { OrderDetailsShipmentPackagesStyles, Shipment } from "@store/Library/Widgets/OrderDetails/OrderDetailsShipment";
import React, { useState } from "react";
import { useSelector } from "react-redux";

type Props = {
    shipment: Shipment;
    styles: OrderDetailsShipmentPackagesStyles;
    showDeliveryStatus: boolean;
    showInvoiceStatus?: boolean;
    openInvoicesInNewTab?: boolean;
};

const ShipmentsRow = ({ shipment, styles, showDeliveryStatus, showInvoiceStatus, openInvoicesInNewTab }: Props) => {
    const [invoiceIsLoading, setInvoiceIsLoading] = useState(false);
    const [shippingDocumentsIsLoading, setShippingDocumentsIsLoading] = useState(false);
    const toasterContext = React.useContext(ToasterContext);
    const trackFileDownload = useTrackFileDownload();
    const trackShippingLink = useTrackLink();
    let dateFormating = translate("Delivery date not available");
    const invoiceHistoryLink = useSelector((state: ApplicationState) =>
        getPageLinkByPageType(state, "InvoiceHistoryPage"),
    );

    const handleOpenInvoice = (invoiceNumber: string) => {
        getDocumentsWrapper(getInvoicePdf, setInvoiceIsLoading, { invoiceNumber, isShipment: true }, toasterContext);
        trackFileDownload({ file_category: "Invoice" });
    };

    const handleDownloadShippingDocuments = (shipmentNumber: string) => {
        getDocumentsWrapper(getShippingDocuments, setShippingDocumentsIsLoading, { shipmentNumber }, toasterContext);
        trackFileDownload({ file_category: "Shipping Document" });
    };

    const handleTrackDocument = () => {
        trackShippingLink({ link_clicked: "Order Details - ETA details" });
    };

    const statusColor = (status: string) => {
        switch (status) {
            case "Shipped":
                return "yellow";
            case "In-Transit":
                return "yellow";
            case "Delivered":
                return "green";
            default:
                return "grey";
        }
    };
    const statusTranslate = (status: string) => {
        switch (status) {
            case "Shipped":
                return translate("Shipped");

            case "In-Transit":
                return translate("In Transit");

            case "Delivered":
                return translate("Delivered");

            default:
                return translate("N/A");
        }
    };

    if (!shipment) {
        return null;
    }

    if (shipment.eta) {
        dateFormating = new Date(shipment.eta.replace("T", ":")).toLocaleString([], {
            year: "numeric",
            month: "numeric",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        });
    }

    return (
        <DataTableRow {...styles.dataTableRow}>
            <DataTableCell {...styles.shipmentDateCell}>
                <StyledWrapper {...styles.loadingWrapper}>
                    <Button
                        {...styles.downloadButton}
                        disabled={!shipment.shipmentNumber || shippingDocumentsIsLoading || !shipment.canDownloadShipmentDocument}
                        onClick={() => handleDownloadShippingDocuments(shipment.shipmentNumber)}
                    >
                        {shippingDocumentsIsLoading ? (
                            <Loader {...styles.buttonLoader} />
                        ) : (
                            <IconMemo {...styles.buttonIcon} />
                        )}

                        {shipment.shipmentNumberDisplay}
                    </Button>
                </StyledWrapper>
            </DataTableCell>
            <DataTableCell {...styles.carrierCell}>
                <LocalizedDateTime
                    dateTime={new Date(shipment.shipmentDate!)}
                    options={{ year: "numeric", month: "numeric", day: "numeric" }}
                />
            </DataTableCell>
            <DataTableCell {...styles.shipViaCell}>{shipment.qtyShipped}</DataTableCell>
            <DataTableCell {...styles.shipViaCell}>{shipment.deliveryUnitPriceDisplay}</DataTableCell>
            <DataTableCell {...styles.trackingNumberCell}>
                {shipment.mapUrl && (
                    <Link
                        href={shipment.mapUrl}
                        disabled={!shipment.mapUrl}
                        target="_blank"
                        onClick={handleTrackDocument}
                    >
                        {shipment.eta}
                    </Link>
                )}
            </DataTableCell>
            <DataTableCell {...styles.shipViaCell}>
                {shipment.invoiceNumber && (
                    <StyledWrapper {...styles.loadingWrapper}>
                        <Button
                            {...styles.downloadButton}
                            onClick={() => handleOpenInvoice(shipment.invoiceNumber)}
                            disabled={!shipment.invoiceNumber || invoiceIsLoading}
                        >
                            {invoiceIsLoading ? (
                                <Loader {...styles.buttonLoader} />
                            ) : (
                                <IconMemo {...styles.buttonIcon} />
                            )}

                            {shipment.invoiceNumberDisplay}
                        </Button>
                    </StyledWrapper>
                )}
            </DataTableCell>
            {showDeliveryStatus && (
                <DataTableCell {...styles.trackingNumberCell}>
                    <Status type={statusColor(shipment.status)} {...styles.statusStyles}>
                        {statusTranslate(shipment.status)}
                    </Status>
                </DataTableCell>
            )}
            {showInvoiceStatus && (
                <DataTableCell {...styles.shipViaCell}>
                    {shipment.invoiceNumberDisplay ? (
                        <Link
                            href={`${invoiceHistoryLink?.url}?invoiceNumber=${shipment.invoiceNumberDisplay}`}
                            target={openInvoicesInNewTab ? "_blank" : ""}
                        >
                            {translate("See Invoice Status")}
                        </Link>
                    ) : (
                        <Typography>{translate("N/A")}</Typography>
                    )}
                </DataTableCell>
            )}
        </DataTableRow>
    );
};

export default ShipmentsRow;
